import { useTranslation } from '@hotjar/translations';
import styled from '@emotion/styled';
import { tokens, Field, Link } from '@hotjar/hotsauce';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { QuestionSelector } from '../../QuestionSelector';
import { useSelectedQuestion } from './useSelectedQuestion';

const StyledField = styled(Field)`
  max-width: 600px;
  width: 100%;
`;

const QuestionBorder = styled.div`
  display: flex;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
  padding: ${tokens.spacer.x3};
  justify-content: space-between;
  box-sizing: content-box;
  gap: ${tokens.spacer.x3};
`;

const ThumbnailContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 144px;
  height: 80px;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};
`;

const Thumbnail = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: scale-down;
`;

const HoverBackground = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  opacity: 0;
  text-align: center;
  justify-content: center;
  &:hover,
  &:focus-within {
    opacity: 1;
  }
  & a {
    text-decoration: none;
  }
`;

type ResultsQuestionSelectorProps = {
  surveyId: number;
  trackingEventName: string;
  allowAll: boolean;
};

export const ResultsQuestionSelector = ({
  surveyId,
  trackingEventName,
  allowAll,
}: ResultsQuestionSelectorProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const hasImageFeature = useFeatureFlag('survey.image_question');

  const selectedQuestion = useSelectedQuestion(surveyId, allowAll);
  const field = (
    <StyledField
      inputId="selectedQuestion"
      labelText={t('feedback:results.results-control.show-results-label')}
    >
      <QuestionSelector
        surveyId={surveyId}
        trackingEventName={trackingEventName}
        allowAll={allowAll}
      />
    </StyledField>
  );

  if (!hasImageFeature) {
    return field;
  }

  return (
    <QuestionBorder>
      {field}
      {selectedQuestion !== 'ALL' && selectedQuestion?.image_thumbnail_url !== undefined && (
        <ThumbnailContainer>
          <Thumbnail
            src={selectedQuestion.image_thumbnail_url}
            aria-label={t('feedback:results.results-control.question-image-thumbnail')}
          />
          <HoverBackground>
            <Link isExternal kind="standalone" href={selectedQuestion.image_url}>
              {t('open-in-new-tab')}
            </Link>
          </HoverBackground>
        </ThumbnailContainer>
      )}
    </QuestionBorder>
  );
};
